import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editAppealRequest } from "services/appealService";
import { AppealRequest } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appeal: AppealRequest;
  onClose: (refetch: boolean) => void;
};

const AppealEditForm: FC<Props> = ({ appeal, onClose }) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<any>({
    ...appeal,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState: any) => ({
      ...(prevState ?? {}),
      [name]: value,
    }));
  };

  const handleFile = (file: File | null) => {
    setFormState((prevState: any) => ({
      ...(prevState ?? {}),
      appeal_summary_file: file,
    }));
  };

  const validateForm = () => {
    if (!formState?.reason) {
      return t("reason_is_required");
    }
    if (!formState?.appeal_summary_text) {
      return t("appeal_summary_is_required");
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    setLoading(true);
    const data = {
      ...formState,
      appeal_summary_file: formState?.appeal_summary_file
        ? formState.appeal_summary_file
        : undefined,
    };
    editAppealRequest(data)
      .then(() => {
        showTooltip(t("successfully_edited"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (appeal) {
      setFormState({
        id: appeal?.id,
        reason: appeal?.reason ?? "",
        appeal_summary_text: appeal?.appeal_summary_text ?? "",
        appeal_summary_file: null,
        defendant_ids: appeal?.defendants?.map((def: any) => def.id) ?? [],
      });
    }
  }, [appeal]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {appeal?.is_cassation
                  ? t("edit_cassation_request", {
                      defaultValue: "Edit Cassation Request",
                    })
                  : t("edit_appeal")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("edit_appeal_form_desc", {
                  defaultValue:
                    "Update the defendants, reason, summary, and supporting file.",
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("select_count_defendant")}
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 h-32"
                name="defendant_ids"
                value={
                  Array.isArray(formState?.defendant_ids)
                    ? formState.defendant_ids.map((item: any) =>
                        typeof item === "object"
                          ? String(item.uu_id ?? item.id)
                          : String(item),
                      )
                    : []
                }
                onChange={(e) => {
                  const selectedIds = Array.from(e.target.selectedOptions).map(
                    (option) => option.value,
                  );
                  setFormState((prev: any) => ({
                    ...prev,
                    defendant_ids: selectedIds.map((id) => id),
                  }));
                }}
                multiple
              >
                {(
                  appeal?.count?.defendants_info ||
                  appeal?.final_verdict?.count_defendant_info
                    ?.defendants_info ||
                  appeal?.case?.case_defendant_links ||
                  []
                )?.map((def: any, idx: number) => (
                  <option key={def.id} value={def?.id}>
                    {def?.defendant_info?.first_name}{" "}
                    {def?.defendant_info?.middle_name}{" "}
                    {def?.defendant_info?.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("reason")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="reason"
                value={formState?.reason ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("appeal_summary")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="appeal_summary_text"
                value={formState?.appeal_summary_text ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>
            <div className="mt-4">
              <FileUpload
                label={t("appeal_request_file")}
                fileTypes="*"
                info={t("upload_files")}
                required={false}
                onChange={handleFile}
              />
            </div>
          </div>
            <div className="flex justify-end gap-3 mt-2 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading")}
                  </>
                ) : (
                  t("save", { defaultValue: "Save Changes" })
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppealEditForm;
